import React, { useState } from 'react'
import cx from 'classnames'
import { removeItemFromCart, setDeliverDetails } from 'actions'
import { Button, TrashIcon } from 'components/common'
import CartInputRow from './CartInputRow'
import s from './Cart.module.css'
import Checkbox from '../common/Checkbox/Checkbox'
import { Fragment } from 'react'
import {
  useAppDispatch,
  useAppSelector
} from 'components/hooks/redux-selectors'
import { DeliveryType } from 'components/common/types'
import { useWindowSize } from 'react-use'

const Cart = ({ onGoToCheckoutClick }) => {
  const {
    items,
    currency,
    delivery,
    pickup,
    usesTwoDestinations,
    businessName,
    usesSingleItemCheckout,
    usesBillingPayment
  } = useAppSelector((state) => state.cart)
  const { deliverDetails } = useAppSelector((state) => state.order)
  const {
    isAuthenticated,
    profileInfo: { isLoading: profileInfoLoading }
  } = useAppSelector((state) => state.auth)

  const { width: innerWidth } = useWindowSize()

  const [open, setOpen] = useState(false)

  const dispatch = useAppDispatch()

  const isMobile = typeof window !== 'undefined' ? innerWidth < 980 : false

  const cartWrapperClass = cx({
    [s['cartWrapper']]: !isMobile,
    [s['cartWrapper--mobile']]: isMobile,
    [s['cartWrapper--mobile-open']]: isMobile && open
  })

  let bottomCartDetails: React.ReactNode = items?.length !== 0 && (
    <div className={s.deliveryPrefer}>
      <div className={s.deliveryRow}>
        <label className={s.orderLabel}>I'll pick up this order *</label>
        {pickup ? (
          <Checkbox
            style={{
              cursor: pickup ? 'pointer' : 'not-allowed'
            }}
            className=""
            checked={deliverDetails.deliveryType === DeliveryType.PICK_UP}
            onChange={(e) => {
              if (e.target.value) {
                dispatch(
                  setDeliverDetails({ deliveryType: DeliveryType.PICK_UP })
                )
              }
            }}
            disabled={!pickup}
          />
        ) : (
          <span className={s.red}>Pickup not available</span>
        )}
      </div>

      <div className={s.deliveryRow}>
        <label className={s.deliverLabel}>Deliver order *</label>
        {delivery ? (
          <Checkbox
            style={{
              cursor: delivery ? 'pointer' : 'not-allowed'
            }}
            className=""
            checked={deliverDetails.deliveryType === DeliveryType.DELIVER}
            onChange={(e) => {
              if (e.target.value) {
                dispatch(
                  setDeliverDetails({ deliveryType: DeliveryType.DELIVER })
                )
              }
            }}
            disabled={!delivery}
          />
        ) : (
          <span className={s.red}>Delivery not available</span>
        )}
      </div>
    </div>
  )

  const handleGoToCheckoutClick = () => {
     if (typeof window.gtag_report_conversion === 'function') {
    console.log("Calling gtag_report_conversion function...");
    window.gtag_report_conversion();
  } else {
    console.warn("gtag_report_conversion is not defined.");
  }
  console.log("Proceeding to checkout...");
    onGoToCheckoutClick()
  }

  let isCheckoutButtonDisabled =
    items?.length === 0 || !deliverDetails.deliveryType

  if (usesTwoDestinations || usesBillingPayment) {
    // If business wants two destinations that means deliveryType is not mandatory anymore
    bottomCartDetails = null
    isCheckoutButtonDisabled = false
  }

  return (
    <>
      <div className={cartWrapperClass}>
        {businessName && (
          <div className="table w-full border-b border-solid border-b-lightsteelblue px-6 py-4">
            <h3 className="table-cell max-w-0 truncate text-center">
              {businessName}
            </h3>
          </div>
        )}

        <div className={s.body}>
          {items?.length === 0 ? (
            <div className={s.emptyIconWrapper}>
              <div className={s.empty}>Your basket is empty</div>
            </div>
          ) : (
            items?.map((item) => {
              const allOptions = item.optionSetItems.flatMap((optionItem) =>
                optionItem.optionSet.flat()
              )

              let itemInfo = ''

              allOptions.forEach((option, index) => {
                itemInfo += `${option.quantity}-${option.itemName}${
                  index !== allOptions.length - 1 ? ',' : ''
                }`
              })

              return (
                <Fragment key={item.id}>
                  <div className={s.itemRow}>
                    <CartInputRow
                      disableIncreaseButton={usesSingleItemCheckout}
                      item={item}
                      removeItemFromCart={() =>
                        dispatch(removeItemFromCart(item.id))
                      }
                    />

                    <div className={s.itemDetails}>
                      <div className={s.itemName}>{item.itemName}</div>

                      <div className={s.price}>
                        <span>
                          {currency} {(item.cost * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <TrashIcon
                      width="20px"
                      height="20px"
                      style={{ marginLeft: '10px', cursor: 'pointer' }}
                      onClick={() => dispatch(removeItemFromCart(item.id))}
                    />
                  </div>
                  <div className={s.itemInfo}>
                    {itemInfo !== '' && `(${itemInfo})`}
                  </div>
                </Fragment>
              )
            })
          )}
        </div>

        {bottomCartDetails}

        <div className={s.footer}>
          <Button
            style={{ height: '50px', marginTop: '0' }}
            isLoading={isAuthenticated ? profileInfoLoading : false}
            text={
              <div className={s.buttonRow}>
                <span className={s.itemCount}>
                  {items.reduce((acc, curr) => curr.quantity + acc, 0)}
                </span>
                <span>Go to Checkout</span>
                <div className={s.fakeDiv} />
              </div>
            }
            variant="active"
            disabled={isCheckoutButtonDisabled}
            onClick={handleGoToCheckoutClick}
          />
        </div>
      </div>

      {isMobile && (
        <div className={s.cartPanel}>
          {bottomCartDetails}

          {!usesTwoDestinations && (
            <Button
              text={open ? 'Buy More' : 'View Basket'}
              onClick={() => setOpen(open ? false : true)}
              className={s.viewBasketButton}
            />
          )}

          <Button
            style={{ height: '50px', marginTop: '0' }}
            isLoading={isAuthenticated ? profileInfoLoading : false}
            text={
              <div className={s.buttonRow}>
                <span className={s.itemCount}>
                  {items.reduce((acc, curr) => curr.quantity + acc, 0)}
                </span>
                <span>{usesTwoDestinations ? 'Next' : 'Go to Checkout'} </span>
                <div className={s.fakeDiv}></div>
              </div>
            }
            variant="active"
            disabled={isCheckoutButtonDisabled}
            onClick={handleGoToCheckoutClick}
          />
        </div>
      )}
    </>
  )
}

export default React.memo(Cart)
